import React from "react";
import { Table, Button } from "react-bootstrap";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import ToPeso from "../components/misc/ToPeso";
import { format, addMonths, addWeeks, addDays, addYears } from "date-fns";


function drawFormattedText(text, x, y, maxWidth, lineHeight, defaultFont, boldFont, fontSize, page, color) {
  const boldRegex = /<b>(.*?)<\/b>/g; // Regex to detect bold tags
  const stripTags = (text) => text.replace(/<b>|<\/b>/g, ""); // Function to remove tags

  // Split text by newlines first
  const paragraphs = text.split("\n");

  let currentY = y;

  for (const paragraph of paragraphs) {
    const words = paragraph.split(" "); // Split paragraph into words
    let currentLine = ""; // Current line being built
    let currentLine2 = ""; // Current line being built
    let lines = []; // Array to store formatted lines

    for (const word of words) {
      const strippedWord = stripTags(word); // Remove tags for drawing
      const testFont = word.match(boldRegex) ? boldFont : defaultFont;
      const testLine = currentLine + (currentLine ? " " : "") + strippedWord;
      const testLine2 = currentLine2 + (currentLine2 ? " " : "") + word;
      const textWidth = testFont.widthOfTextAtSize(testLine, fontSize);

      if (textWidth > maxWidth) {
        lines.push(currentLine2); // Save the current line
        currentLine = strippedWord; // Start a new line
        currentLine2 = word; // Start a new line
      } else {
        currentLine = testLine; // Add word to the current line
        currentLine2 = testLine2; // Start a new line
      }
    }

    // Push the last line
    if (currentLine) {
      lines.push(currentLine2);
    }

    // Draw each line with formatting
    for (const line of lines) {
      let offsetX = x; // Start x-coordinate
      const formattedWords = line.split(" "); // Split into words for formatting

      for (const word of formattedWords) {
        const isBold = word.match(boldRegex); // Check if word is bold
        const strippedWord = stripTags(word); // Remove tags for drawing
        const wordFont = isBold ? boldFont : defaultFont; // Apply bold font if detected

        // Draw the word
        page.drawText(strippedWord, {
          x: offsetX,
          y: currentY,
          size: fontSize,
          font: wordFont, // Correct font applied
          color: color,
        });

        // Adjust x-position for the next word
        offsetX += wordFont.widthOfTextAtSize(strippedWord + " ", fontSize);
      }

      currentY -= lineHeight; // Move down for the next line
    }

    currentY -= lineHeight; // Additional spacing between paragraphs
  }
}

// Fix the numberToWords function to handle potential non-number inputs
const numberToWords = (num) => {
  // Make sure num is a valid number
  const validNum = parseFloat(num);
  
  if (isNaN(validNum)) {
    return "<b>Zero</b>";
  }

  if (validNum === 0) return "<b>Zero</b>";

  const belowTwenty = [
    "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];
  const thousands = ["", "Thousand", "Million", "Billion"];

  let word = "";

  const convertChunk = (n) => {
    let chunk = "";
    if (n >= 100) {
      chunk += belowTwenty[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      chunk += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      chunk += belowTwenty[n] + " ";
    }
    return chunk.trim();
  };

  // Separate integer and decimal parts
  const [integerPart, decimalPart] = validNum.toString().split(".");

  let i = 0;
  let intNum = parseInt(integerPart, 10);

  // Convert integer part
  while (intNum > 0) {
    const chunk = intNum % 1000;
    if (chunk > 0) {
      word = convertChunk(chunk) + (thousands[i] ? " " + thousands[i] : "") + " " + word;
    }
    intNum = Math.floor(intNum / 1000);
    i++;
  }

  word = word.trim();

  // Handle decimal part
  if (decimalPart) {
    const decimalNumber = parseInt(decimalPart.padEnd(2, '0').slice(0, 2), 10); // Ensure two digits for cents
    const decimalWords = decimalNumber > 0
      ? convertChunk(decimalNumber)
      : "Zero";
    word += ` and ${decimalWords} Cents`;
  }

  // Wrap each word with <b></b>
  return word
    .split(" ")
    .map((w) => `<b>${w}</b>`)
    .join(" ");
};

// Utility function for rounding up - fix to ensure it always receives a number
const roundUp = (amount) => {
  // Ensure amount is a number
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) {
    return 0;
  }
  
  const amountString = numAmount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== "0") {
    return parseFloat((numAmount + 0.01).toFixed(2));
  }

  return numAmount;
};

// Function to calculate due dates and amounts - fix type conversion
const calculateDueDates = (startDate, interval, term, amount, interest, adv) => {
  const dueDates = [];
  
  // Ensure all numeric values are properly converted from potential strings
  const numAmount = parseFloat(amount);
  const numInterest = parseFloat(interest);
  const numTerm = parseInt(term, 10);
  
  // Now use the converted values in calculations
  const principalAmount = numAmount / numTerm;
  const interestAmountInitial = (adv) ? (numAmount * numInterest) / 100 : 0;
  const interestAmountMinusAdvance = (((numAmount * numInterest) / 100) * numTerm) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / numTerm;
  
  let currentDate = new Date(startDate);

  for (let i = 0; i < numTerm; i++) {
    switch (interval.toLowerCase()) {
      case "monthly":
        currentDate = addDays(currentDate, 31);
        break;
      case "bimonthly": // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case "weekly":
        currentDate = addWeeks(currentDate, 1);
        break;
      case "quarterly":
        currentDate = addMonths(currentDate, 3);
        break;
      case "semiannual":
        currentDate = addMonths(currentDate, 6);
        break;
      case "yearly":
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error("Invalid interval");
    }

    dueDates.push({
      amortizationNumber: i + 1,
      amortizationDate: format(currentDate, "MM/dd/yyyy"),
      principal: roundUp(principalAmount),
      interest: roundUp(interestAmount),
      total: roundUp(principalAmount + interestAmount),
    });
  }

  return dueDates;
};

// React Component
function DocumentAuthorityToDeduct({ applicantDetails, loanProcessingDetails }) {
  if (!applicantDetails) {
    return null;
  }
  
  const generatePDF = async (loan) => {
    try {
      // Ensure proper number conversions
      const numAmountGranted = parseFloat(loan.amountGranted);
      const numInterestRate = parseFloat(loan.interestRate);
      const numLoanTerm = parseInt(loan.loanTerm, 10);

      // Create schedule with proper number values
      const schedule = calculateDueDates(
        loan.dateGranted,
        loan.loanInterval,
        numLoanTerm,
        numAmountGranted,
        numInterestRate,
        loan.advanceInterest
      );

      console.log(schedule);
      
      const existingPdfBytes = await fetch("/pdftemplates/authority-to-deduct.pdf").then(res =>
          res.arrayBuffer()
      );

      // Load PDF with pdf-lib
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      // Embed fonts
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Get the first page
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];

      const companyName = '<b>HAYAHAY</b> <b>LENDING</b> <b>CORP</b>';
      
      // Calculate total amount correctly with proper number values
      const totalAmount = numAmountGranted + ((numAmountGranted * (numInterestRate / 100)) * numLoanTerm);
      
      // Ensure we have a proper number value for schedule[0].total
      const scheduleAmount = schedule.length > 0 ? parseFloat(schedule[0].total) : 0;

      const longText = `
      Subject to the approval and my availment of a loan with ${companyName}, I hereby assign in favor of ${companyName} the sum of ${numberToWords(totalAmount)} <b>Pesos</b> <b>Only</b> <b>(P${totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')})</b> from my salary/pension and other remuneration EVERY quencena / month and I, therefore, authorize the latter or any of its duly authorized representative to collect ${numberToWords(scheduleAmount)} <b>Pesos</b> <b>Only</b> <b>(P${scheduleAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')})</b> from my salary/pension and remit to ${companyName} through salary/pension deduction on or before <b>${schedule[0]?.amortizationDate || "N/A"}</b> and every quencena / month thereafter without need of demand thereof, until the entire obligation with a principal amount of ${numberToWords(numAmountGranted)} <b>Pesos</b> <b>Only</b> <b>(P${numAmountGranted.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')})</b> is fully paid. 
      It shall be understood that upon my resignation, dismissal or separation from service for any reason or absence without leave for no more than two weeks, the whole amount of my loan shall be due and demandable. In this event, I also authorize the ${companyName}, or any of its duly authorized representative to collect any and all amount due me as termination, separation or gratuity pay or compensation of whatever nature to the extent of my then outstanding obligation. 
      I also authorize ${companyName} to collect any specified amount for the purpose of updating my account, thereat. 
      This assignment and authority to collect is absolute and irrevocable.
      `;

      drawFormattedText(
        longText,
        37, // Starting x-coordinate
        662, // Starting y-coordinate
        538, // Max width for wrapping
        12, // Line height
        helveticaFont, // Default font
        helveticaFontBold, // Bold font
        9, // Font size
        firstPage, // The PDF page
        rgb(0, 0, 0) // Text color
      );
      
      // Save the modified PDF
      const pdfBytes = await pdfDoc.save();

      // Use FileSaver to download the PDF
      saveAs(new Blob([pdfBytes]), `Authority_To_Deduct_${applicantDetails.firstName.toUpperCase()}_${applicantDetails.lastName.toUpperCase()}_${loan.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('An error occurred while generating the PDF. Please try again.');
    }
  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>

      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={parseFloat(loan.amountGranted).toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => generatePDF(loan)}>
                    Generate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}
    </>
  );
}

export default DocumentAuthorityToDeduct;
